import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import Avatar from '../../components/common/Avatar';
import { createOrder, verifyAndBook, recoverBooking, checkReturningPatient, getSlotById } from '../../services/patientService';
import api from '../../services/api';
import { formatTime12, formatDate, formatCurrency } from '../../utils/helpers';
import { PAYMENT_METHODS } from '../../utils/constants';
import { loadCashfree } from '../../hooks/useCashfree';
import { useAuth } from '../../context/AuthContext';

// ── Step Bar ───────────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = [
    { num:1, label:'Details',  icon:'fa-user' },
    { num:2, label:'Payment',  icon:'fa-credit-card' },
    { num:3, label:'Done',     icon:'fa-circle-check' },
  ];
  return (
    <div className="flex items-center px-6 py-4 bg-white border-b border-border">
      {steps.map((s, i) => {
        const done   = step > s.num;
        const active = step === s.num;
        return (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                done   ? 'bg-green-500'
                : active ? 'bg-primary shadow-lg shadow-primary/30'
                : 'bg-slate-100'
              }`}>
                {done
                  ? <i className="fa-solid fa-check text-white text-sm" />
                  : <i className={`fa-solid ${s.icon} text-xs ${active ? 'text-white' : 'text-slate-400'}`} />}
              </div>
              <span className={`text-[9px] font-bold uppercase tracking-wide ${
                done ? 'text-green-600' : active ? 'text-primary' : 'text-slate-400'
              }`}>{s.label}</span>
            </div>
            {i < 2 && (
              <div className={`flex-1 h-0.5 mx-2 rounded-full transition-all ${
                step > s.num ? 'bg-green-400' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Doctor Info Strip ──────────────────────────────────────────────────────
function DoctorStrip({ doctor, slot, type }) {
  return (
    <div className="bg-white px-4 py-3 border-b border-border flex items-center gap-3">
      <Avatar src={doctor?.profileImg} name={doctor?.name} size="sm" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-text-primary truncate">{doctor?.name}</p>
        <p className="text-xs text-text-muted">{formatTime12(slot?.startTime)} · {formatDate(slot?.date)}</p>
      </div>
      <span className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${
        type === 'video' ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
      }`}>
        <i className={`fa-solid ${type === 'video' ? 'fa-video' : 'fa-hospital'} text-[10px]`} />
        {type === 'video' ? 'Video' : 'Clinic'}
      </span>
    </div>
  );
}

// ── Patient Form Row ───────────────────────────────────────────────────────
function PatientRow({ patient, index, onChange, onRemove, canRemove, isReturning, returningChecked }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary-light flex items-center justify-center text-xs font-bold text-primary">
            {index + 1}
          </div>
          <span className="text-sm font-bold text-text-primary">
            {index === 0 ? 'Primary Patient' : `Patient ${index + 1}`}
          </span>
        </div>
        {canRemove && (
          <button onClick={onRemove}
            className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
            <i className="fa-solid fa-xmark text-danger text-xs" />
          </button>
        )}
      </div>

      <div>
        <label className="input-label">Full Name <span className="text-danger">*</span></label>
        <input value={patient.name} onChange={e => onChange('name', e.target.value)}
          placeholder="Patient's full name" className="input" autoFocus={index === 0} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="input-label">Age <span className="text-danger">*</span></label>
          <input type="number" value={patient.age} min={1} max={120}
            onChange={e => onChange('age', e.target.value)}
            placeholder="Age" className="input" />
        </div>
        <div>
          <label className="input-label">Gender <span className="text-danger">*</span></label>
          <div className="flex gap-1.5 mt-1">
            {['M','F','O'].map((g, i) => {
              const full = ['Male','Female','Other'][i];
              return (
                <button key={g} onClick={() => onChange('gender', full)}
                  className={`flex-1 py-2.5 text-xs rounded-xl border-2 font-bold transition-all ${
                    patient.gender === full
                      ? 'border-primary bg-primary text-white'
                      : 'border-border text-slate-500'
                  }`}>{g}</button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Address field — required for all appointment types */}
      <div>
        <label className="input-label">Address <span className="text-danger">*</span></label>
        <input value={patient.address || ''} onChange={e => onChange('address', e.target.value)}
          placeholder="House no., Street, City" className="input" />
      </div>

      {/* Only show returning patient section if DB confirms they have visited before */}
      {!returningChecked ? (
        // Loading state - checking DB
        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-border">
          <i className="fa-solid fa-spinner animate-spin text-slate-400 text-xs" />
          <span className="text-xs text-text-muted">Checking patient history...</span>
        </div>
      ) : isReturning && index === 0 ? (
        // Confirmed returning patient - show toggle to apply/remove discount
        <div className="p-3 bg-green-50 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-rotate-left text-white text-xs" />
              </div>
              <div>
                <p className="text-sm font-bold text-green-800">Returning Patient ✓</p>
                <p className="text-xs text-green-600">You have visited this doctor before</p>
              </div>
            </div>
            <button onClick={() => onChange('isOldPatient', !patient.isOldPatient)}
              className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                patient.isOldPatient ? 'bg-green-500' : 'bg-slate-200'
              }`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                patient.isOldPatient ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          {patient.isOldPatient && (
            <p className="text-xs text-green-700 font-semibold mt-2 flex items-center gap-1">
              <i className="fa-solid fa-tag text-green-600" />
              Returning patient discount will be applied
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Payment Method Card ────────────────────────────────────────────────────
function PayMethodCard({ method, selected, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-3 w-full p-4 rounded-2xl border-2 text-left transition-all active:scale-98 ${
        selected ? 'border-primary bg-primary/5' : 'border-border bg-white'
      }`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-primary' : 'bg-slate-100'
      }`}>
        <i className={`fa-solid ${method.icon} ${selected ? 'text-white' : 'text-slate-500'} text-base`} />
      </div>
      <div className="flex-1">
        <p className={`text-sm font-bold ${selected ? 'text-primary' : 'text-text-primary'}`}>{method.label}</p>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected ? 'border-primary bg-primary' : 'border-slate-300'
      }`}>
        {selected && <i className="fa-solid fa-check text-white text-[8px]" />}
      </div>
    </button>
  );
}

// ── Order Summary ──────────────────────────────────────────────────────────
function OrderSummary({ summary, windowExpired, windowExpiredInfo }) {
  return (
    <div className="bg-white rounded-2xl border border-border overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-slate-50">
        <p className="text-sm font-bold text-text-primary">Order Summary</p>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Doctor's Fee</span>
          <span className="font-semibold text-text-primary">{formatCurrency(summary.doctorFee)}</span>
        </div>
        {/* Window expired notice — returning but discount no longer valid */}
        {windowExpired && windowExpiredInfo && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
            <i className="fa-solid fa-clock text-amber-500 text-sm flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800">Returning Patient — Discount Expired</p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                You visited before, but the returning patient discount was valid for {windowExpiredInfo.validityMonths} month{windowExpiredInfo.validityMonths !== 1 ? 's' : ''} from your last visit.
                {windowExpiredInfo.expiredDaysAgo ? ` The offer expired ${windowExpiredInfo.expiredDaysAgo} day${windowExpiredInfo.expiredDaysAgo !== 1 ? 's' : ''} ago.` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Discount row in order summary */}
        {summary.discountAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <i className="fa-solid fa-tag text-[10px]" />Returning Patient Discount
            </span>
            <span className="text-green-600 font-semibold">−{formatCurrency(summary.discountAmount)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Convenience Charge</span>
          <span className="text-text-primary">{formatCurrency(summary.convenienceCharge)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">GST ({summary.gstPercent || 18}%)</span>
          <span className="text-text-primary">{formatCurrency(summary.gst)}</span>
        </div>
        <div className="pt-2 border-t border-border flex justify-between items-center">
          <span className="font-bold text-text-primary">Total Payable</span>
          <span className="text-xl font-black text-primary">{formatCurrency(summary.totalAmount)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Main BookingPage ───────────────────────────────────────────────────────
export default function BookingPage() {
  const { doctorId }    = useParams();
  const { state }       = useLocation();
  const navigate        = useNavigate();
  const { user }        = useAuth();
  // Cashfree checkout is opened directly in pay() using the SDK

  const doctor = state?.doctor;
  const slot   = state?.slot;
  const type   = state?.type || 'clinic';
  const slotId = state?.slotId; // set when coming back from login redirect

  const [resolvedSlot,   setResolvedSlot]   = useState(slot   || null);
  // eslint-disable-next-line no-unused-vars
  const [resolvedDoctor, setResolvedDoctor] = useState(doctor || null);
  const [resolving,      setResolving]      = useState(!slot && !!slotId);

  const [step, setStep]         = useState(1);
  const [patients, setPatients] = useState([{
    name: user?.name || '', age: user?.age || '', gender: user?.gender || '', address: '', isOldPatient: false,
  }]);
  const [summary, setSummary]   = useState(null);
  const [payMethod, setPayMethod] = useState('upi');
  const [orderId,           setOrderId]           = useState(null);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [orderRes_env,     setOrderResEnv]       = useState('SANDBOX');
  // eslint-disable-next-line no-unused-vars
  const [keyId,            setKeyId]             = useState('');
  const [loading, setLoading]   = useState(false);
  const [isFree, setIsFree]         = useState(false);
  const [isReturningPatient, setIsReturningPatient] = useState(false);
  const [returningChecked, setReturningChecked]     = useState(false);
  const [windowExpired,     setWindowExpired]        = useState(false);
  const [windowExpiredInfo, setWindowExpiredInfo]    = useState(null);

  // Resolve slot + doctor from slotId (when coming back from login redirect)
  useEffect(() => {
    if (!slotId || (slot && doctor)) return;
    setResolving(true);
    getSlotById(slotId)
      .then(async res => {
        const fetchedSlot = res.slot;
        setResolvedSlot(fetchedSlot);
        if (!doctor && fetchedSlot?.doctorId) {
          try {
            const docRes = await api.get(`/doctors/${fetchedSlot.doctorId}`);
            setResolvedDoctor(docRes.doctor || docRes);
          } catch { /* use slot info only */ }
        }
      })
      .catch(() => navigate(`/doctor/${doctorId}`, { replace: true }))
      .finally(() => setResolving(false));
  // eslint-disable-next-line
  }, [slotId]);

  // Auto-check if patient is returning (has previous appointments with this doctor)
  useEffect(() => {
    if (!doctorId || !user?._id) return;
    checkReturningPatient(doctorId)
      .then(res => {
        if (res.isReturning) {
          if (res.windowExpired) {
            // Returning but window expired — don't apply discount
            setWindowExpired(true);
            setWindowExpiredInfo({ validityMonths: res.validityMonths, expiredDaysAgo: res.expiredDaysAgo });
            setIsReturningPatient(false);
          } else {
            setIsReturningPatient(true);
            setWindowExpired(false);
            setPatients(prev => prev.map((p, i) => i === 0 ? { ...p, isOldPatient: true } : p));
          }
        }
        setReturningChecked(true);
      })
      .catch(() => setReturningChecked(true));
  // eslint-disable-next-line
  }, [doctorId, user?._id]);

  // Guard: redirect if no slot resolved and not loading
  useEffect(() => {
    if (resolving) return;
    const t = setTimeout(() => {
      if (!resolvedSlot) navigate(`/doctor/${doctorId}`, { replace: true });
    }, 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [resolvedSlot, resolving]);


  const addPatient = () => {
    const max = Math.min(3, resolvedSlot?.maxPatients || 1);
    if (patients.length >= max) { toast.error(`Max ${max} patient${max > 1 ? 's' : ''} per booking`); return; }
    setPatients(p => [...p, { name:'', age:'', gender:'', address:'', isOldPatient:false }]);
  };

  const updatePatient = (i, key, val) =>
    setPatients(prev => prev.map((p, idx) => idx === i ? { ...p, [key]: val } : p));

  const removePatient = i =>
    setPatients(prev => prev.filter((_, idx) => idx !== i));

  const goToPayment = async () => {
    for (let i = 0; i < patients.length; i++) {
      const p   = patients[i];
      const age = Number(p.age);
      if (!p.name?.trim())        { toast.error(`Patient ${i+1}: name is required`); return; }
      if (!p.age || isNaN(age) || age < 1 || age > 120) { toast.error(`Patient ${i+1}: valid age required (1-120)`); return; }
      if (!p.gender)              { toast.error(`Patient ${i+1}: please select gender`); return; }
      if (!p.address?.trim())     { toast.error(`Patient ${i+1}: address is required`); return; }
    }
    setLoading(true);
    try {
      const patientsClean = patients.map(p => ({ ...p, age: Number(p.age) }));
      const orderRes = await createOrder({ doctorId, slotId: resolvedSlot._id, patients: patientsClean, type });
      setOrderId(orderRes.orderId);
      setPaymentSessionId(orderRes.paymentSessionId || null);
      setOrderResEnv(orderRes.environment || 'SANDBOX');
      setKeyId(orderRes.keyId || null);   // legacy, unused in Cashfree flow
      setSummary(orderRes.summary);
      setIsFree(orderRes.isFree || false);
      setStep(2);
    } catch (err) {
      const msg = err.message || 'Something went wrong';
      // Only treat as session expired if the error specifically mentions token/expired
      const isTokenExpired = msg.toLowerCase().includes('token expired') ||
                             msg.toLowerCase().includes('jwt expired') ||
                             msg.toLowerCase().includes('invalid token');
      if (isTokenExpired) {
        toast.error('Session expired. Please login again.');
        sessionStorage.setItem('pendingBooking', JSON.stringify({ doctorId, slotId: resolvedSlot._id, type }));
        window.dispatchEvent(new Event('auth:logout'));
        navigate('/login', { replace: true, state: { from: `/book/${doctorId}` } });
      } else {
        toast.error(msg || 'Booking failed. Please try again.');
      }
    }
    finally { setLoading(false); }
  };

  const pay = async () => {
    if (!orderId) { toast.error('Order not created'); return; }

    const bookingPayload = {
      doctorId, slotId: resolvedSlot._id,
      patients: patients.map(p => ({ ...p, age: Number(p.age) })), type,
      doctorFee:         summary.doctorFee,
      discountAmount:    summary.discountAmount || 0,
      discountType:      summary.discountType   || null,
      convenienceCharge: summary.convenienceCharge,
      gst:               summary.gst,
      totalAmount:       summary.totalAmount,
      paymentMethod:     payMethod,
    };

    // ── Free booking ─────────────────────────────────────────────────────
    if (isFree) {
      setLoading(true);
      try {
        const res = await verifyAndBook({
          ...bookingPayload,
          cashfreeOrderId:   orderId,
          cashfreePaymentId: 'FREE_PAYMENT',
          paymentMethod:     'free',
        });
        navigate('/booking-success', { state: { booking: res, doctor, slot, type } });
      } catch (err) {
        const msg = err.message || '';
        if (msg.includes('401') || msg.includes('403') || msg.includes('Session')) {
          toast.error('Session expired — please login again');
          navigate('/login', { state: { from: `/book/${doctorId}` } });
        } else {
          toast.error(msg || 'Booking failed. Please try again.');
        }
      } finally { setLoading(false); }
      return;
    }

    // ── Paid booking — open Cashfree checkout ────────────────────────────
    setLoading(true);
    let cashfreeInstance;
    try {
      cashfreeInstance = await loadCashfree(orderRes_env || 'SANDBOX');
    } catch (err) {
      toast.error('Payment SDK failed to load. Check your internet and try again.');
      setLoading(false);
      return;
    }

    // Remember the order: if this tab dies after payment, the return page / server recovery finishes it
    try { localStorage.setItem('pendingCashfreeOrder', orderId); } catch (_) {}

    const finishBooking = async (paymentId) => {
      setLoading(true);
      toast('Verifying payment...');
      try {
        const res = await verifyAndBook({
          ...bookingPayload,
          cashfreeOrderId:   orderId,
          cashfreePaymentId: paymentId || '',
        });
        try { localStorage.removeItem('pendingCashfreeOrder'); } catch (_) {}
        navigate('/booking-success', { state: { booking: res, doctor, slot, type } });
      } catch (vErr) {
        const msg = vErr.message || '';
        if (/already used/i.test(msg)) {
          // server (webhook/recovery) already created the booking for this paid order
          try { localStorage.removeItem('pendingCashfreeOrder'); } catch (_) {}
          toast.success('Payment received - your booking is confirmed!');
          navigate('/appointments');
        } else if (msg.includes('401') || msg.includes('403') || msg.includes('Session')) {
          toast.error('Session expired - please login again');
          navigate('/login', { state: { from: '/appointments' } });
        } else {
          toast.error(msg || 'Payment done but booking failed. It will be completed automatically - check My Appointments.');
        }
      }
    };

    try {
      // Popup mode: we stay on the page and get a JS result back.
      const result = await cashfreeInstance.checkout({
        paymentSessionId,
        redirectTarget: '_modal',
      });

      if (result?.error) {
        // Popup closed or payment failed. If money was deducted anyway, the server
        // books it automatically (webhook + background recovery) - tell the user.
        toast.error((result.error?.message || 'Payment cancelled or failed.') +
          ' If money was deducted, your booking is confirmed automatically within a few minutes.');
        setLoading(false);
        return;
      }

      if (result?.paymentDetails) {
        await finishBooking(result.paymentDetails?.paymentId);
      } else {
        // Redirect-style / unknown result: ask the server to complete the order if it is paid
        setLoading(true);
        toast('Checking payment status...');
        let done = false;
        for (let k = 0; k < 4 && !done; k++) {
          try {
            const rec = await recoverBooking(orderId);
            if (rec?.success) { done = true; try { localStorage.removeItem('pendingCashfreeOrder'); } catch (_) {} navigate('/appointments'); }
          } catch (_) { await new Promise(r => setTimeout(r, 2500)); }
        }
        if (!done) toast.error('Payment not confirmed yet. If money was deducted, your booking will appear in My Appointments shortly.');
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!doctor || !slot) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showBack title={`Book Appointment`} />
      <div className="pt-14">
        <StepBar step={step} />
        <DoctorStrip doctor={doctor} slot={slot} type={type} />

        <div className="px-4 py-4 space-y-4 pb-32">

          {/* ── STEP 1: Patient Details ── */}
          {step === 1 && (
            <>
              <div className="flex items-center justify-between">
                {(resolvedSlot?.maxPatients || 1) > 1 && (
                  <p className="text-base font-bold text-text-primary">Who is this for?</p>
                )}
                {patients.length < Math.min(3, resolvedSlot?.maxPatients || 1) && (resolvedSlot?.maxPatients || 1) > 1 && (
                  <button onClick={addPatient}
                    className="flex items-center gap-1.5 text-xs font-bold text-primary ml-auto">
                    <i className="fa-solid fa-plus text-[10px]" />Add Patient
                  </button>
                )}
              </div>
              {patients.map((p, i) => (
                <PatientRow key={i} patient={p} index={i}
                  onChange={(k, v) => updatePatient(i, k, v)}
                  onRemove={() => removePatient(i)}
                  canRemove={patients.length > 1}
                  isReturning={isReturningPatient}
                  returningChecked={returningChecked} />
              ))}

              {/* Slot info reminder */}
              <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-2xl border border-teal-200">
                <i className="fa-solid fa-clock text-teal-600 text-base" />
                <div>
                  <p className="text-xs font-bold text-teal-800">Slot Time</p>
                  <p className="text-sm font-semibold text-teal-700">
                    {formatTime12(resolvedSlot?.startTime)} · {formatDate(resolvedSlot?.date)}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2: Payment ── */}
          {step === 2 && summary && (
            <>
              <p className="text-base font-bold text-text-primary">Choose Payment Method</p>
              <div className="space-y-2">
                {PAYMENT_METHODS.map(m => (
                  <PayMethodCard key={m.value} method={m}
                    selected={payMethod === m.value}
                    onClick={() => setPayMethod(m.value)} />
                ))}
              </div>

              <OrderSummary summary={summary} windowExpired={windowExpired} windowExpiredInfo={windowExpiredInfo} />

              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-xl">
                <i className="fa-solid fa-shield-halved text-green-600 text-sm" />
                <p className="text-xs text-slate-600 font-medium">
                  Secure payment via Cashfree. Your data is encrypted.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sticky bottom ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border px-4 py-4"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
        {step === 1 ? (
          <button onClick={goToPayment} disabled={loading}
            className="w-full py-4 rounded-2xl bg-primary text-white font-bold text-base shadow-lg shadow-primary/25 active:scale-98 transition-all disabled:opacity-60">
            {loading
              ? <span className="flex items-center justify-center gap-2">
                  <i className="fa-solid fa-spinner animate-spin" />Loading...
                </span>
              : <span>Continue to Payment <i className="fa-solid fa-arrow-right ml-2" /></span>}
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1 mb-1">
              <span className="text-sm text-text-muted font-medium">Total to pay</span>
              <span className="text-xl font-black text-primary">{summary && formatCurrency(summary.totalAmount)}</span>
            </div>
            <button onClick={pay} disabled={loading}
              className="w-full py-4 rounded-2xl bg-primary text-white font-bold text-base shadow-lg shadow-primary/25 active:scale-98 transition-all disabled:opacity-60">
              {loading
                ? <span className="flex items-center justify-center gap-2">
                    <i className="fa-solid fa-spinner animate-spin" />Processing...
                  </span>
                : <span><i className={`fa-solid ${isFree ? 'fa-calendar-check' : 'fa-lock'} mr-2 text-sm`} />{isFree ? 'Confirm Booking (Free)' : 'Pay Securely'}</span>}
            </button>
            <button onClick={() => setStep(1)}
              className="w-full py-2.5 text-sm text-text-muted font-medium">
              ← Edit patient details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
